import React from 'react';
import { useQuery } from '@apollo/client';
import { CustomFormField } from 'components/field/form/CustomFormField';
import { useValue } from 'components/form/hook/useValue';
import { Button } from 'components/Button';
import { Modal } from 'components/modal/Modal';
import { FormDate } from 'components/form/FormDate';
import { FormGroup } from 'components/form/FormGroup';
import { FormField } from 'components/form/FormField';
import { toFormattedDate } from 'components/DateTime';
import { SaleItemAvailabilityQuery } from 'graphql/Deal/queries/SaleItemAvailabilityQuery';
import { timeSlotsOverlap, toTimeSlot } from 'helpers/timeSlot';
import { formatConfigTimeForCalendar } from 'modules/calendar/calendarEventDates';
import './DealItemEditModal.scss';
/**
 * @component DealItemEditModal
 * @since 1.0.0
 * @hidden
 */
export function DealItemEditModal(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { customFields, item, type, onClose, hasTime, timeSlotCheck } = props;
    const [value, setValue] = useValue('dealItem', item, Object.assign(Object.assign({}, item), { fieldValues: (_a = item.fieldValues) === null || _a === void 0 ? void 0 : _a.map((fv) => (Object.assign(Object.assign({}, fv), { value: JSON.parse(fv.value).value }))) }));
    /**
     * @component getField
     * @since 1.0.0
     * @hidden
     */
    function getField(field) {
        var _a, _b;
        const v = (_b = (_a = item.fieldValues) === null || _a === void 0 ? void 0 : _a.find((fieldValue) => fieldValue.field.id === field.id)) === null || _b === void 0 ? void 0 : _b.value;
        const f = Object.assign(Object.assign({}, field), { value: v !== null && v !== void 0 ? v : JSON.stringify({ value: '' }) });
        return f;
    }
    /**
     * @function updateDealItemFieldValues
     * @since 1.0.0
     * @hidden
     */
    const updateDealItemFieldValues = (fieldValue, field) => {
        var _a;
        const newValues = addOrUpdateFieldValue((_a = value.fieldValues) !== null && _a !== void 0 ? _a : [], field.id, fieldValue);
        setValue(Object.assign(Object.assign({}, value), { fieldValues: newValues }));
    };
    /**
     * @function addOrUpdateFieldValue
     * @since 1.0.0
     * @hidden
     */
    const addOrUpdateFieldValue = (fieldValues, fieldId, value) => {
        const exist = !!fieldValues.find((fv) => fv.field.id === fieldId);
        const formattedValue = JSON.stringify({ value: value[fieldId] });
        if (exist) {
            return fieldValues.map((fv) => fv.field.id === fieldId ? Object.assign(Object.assign({}, fv), { value: formattedValue }) : fv);
        }
        else {
            return [
                ...fieldValues,
                { id: fieldId, value: formattedValue, field: { id: fieldId } }
            ];
        }
    };
    const saleItem = type === 'option' ? item.item : null;
    // Option réservable : on charge les réservations du jour qui la bloquent
    // (elle-même ou un item lié) pour refuser une plage déjà prise.
    const exclusiveTimeSlot = Boolean(hasTime && timeSlotCheck && (saleItem === null || saleItem === void 0 ? void 0 : saleItem.exclusive));
    const timeSlotDate = (_b = timeSlotCheck === null || timeSlotCheck === void 0 ? void 0 : timeSlotCheck.date) !== null && _b !== void 0 ? _b : null;
    const { data: availabilityData } = useQuery(SaleItemAvailabilityQuery, {
        variables: {
            input: {
                requests: [{ saleItem: saleItem === null || saleItem === void 0 ? void 0 : saleItem.id, dateStart: timeSlotDate }],
                excludeDeal: (_c = timeSlotCheck === null || timeSlotCheck === void 0 ? void 0 : timeSlotCheck.dealId) !== null && _c !== void 0 ? _c : null
            }
        },
        skip: !exclusiveTimeSlot || !saleItem || !timeSlotDate,
        fetchPolicy: 'network-only'
    });
    const bookings = exclusiveTimeSlot
        ? ((_f = (_e = (_d = availabilityData === null || availabilityData === void 0 ? void 0 : availabilityData.saleItemAvailability) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.conflicts) !== null && _f !== void 0 ? _f : [])
        : [];
    const configuration = value;
    const slot = toTimeSlot(configuration.startTime, configuration.endTime);
    // Sans heure, l'option réserve toute la journée.
    const conflicting = bookings.filter((booking) => {
        if (booking.wholeDay || !slot) {
            return true;
        }
        const bookedSlot = toTimeSlot(booking.startTime, booking.endTime);
        return bookedSlot != null && timeSlotsOverlap(slot, bookedSlot);
    });
    const conflict = conflicting[0];
    const formatTimeRange = (startTime, endTime) => [
        formatConfigTimeForCalendar(startTime),
        formatConfigTimeForCalendar(endTime)
    ]
        .filter(Boolean)
        .join(' — ') || t('All day');
    // Les titres par défaut contiennent déjà le numéro : on ne le répète pas.
    const formatBookingDeal = (deal) => {
        var _a;
        const number = deal.number ? `#${deal.number}` : '';
        const title = (_a = deal.title) !== null && _a !== void 0 ? _a : '';
        return title && number && !title.includes(number)
            ? `${number} ${title}`
            : title || number;
    };
    const conflictTitle = !conflict
        ? null
        : conflict.wholeDay
            ? t('This item is already booked for the whole day.')
            : !slot
                ? t('Without a time, this option books the whole day.')
                : t('This time slot overlaps a booking.');
    const conflictHint = !conflict
        ? null
        : conflict.wholeDay
            ? t('Choose another date or another item.')
            : t('Choose a time slot outside the bookings below.');
    const footerActions = (React.createElement("div", { className: "deal-item-edit-modal-container-footer" },
        type !== 'item' && (React.createElement(Button, { label: type === 'option'
                ? t('Apply to all options')
                : t('Apply to all packages'), style: "outline", disabled: conflict != null, onPress: () => {
                if (props.onUpdateItem) {
                    props.onUpdateItem(value, true);
                }
            } })),
        React.createElement(Button, { label: t('Save'), style: "primary", disabled: conflict != null, onPress: () => {
                if (props.onUpdateItem) {
                    props.onUpdateItem(value, false);
                }
            } })));
    return (React.createElement("div", { className: "deal-item-edit-modal" },
        React.createElement(Modal, { title: t('Edit fields'), picto: "edit", onClose: onClose, showFooterAction: true, footerActions: footerActions },
            React.createElement("div", { className: "deal-item-edit-modal-content" },
                hasTime && (React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormDate, { name: "startTime", label: t('Start time'), timeOnly: true, value: (_g = value.startTime) !== null && _g !== void 0 ? _g : null, placeholder: "hh:mm", onChange: (v) => setValue(Object.assign(Object.assign({}, value), { startTime: v })) })),
                    React.createElement(FormField, null,
                        React.createElement(FormDate, { name: "endTime", label: t('End time'), timeOnly: true, value: (_h = value.endTime) !== null && _h !== void 0 ? _h : null, placeholder: "hh:mm", onChange: (v) => setValue(Object.assign(Object.assign({}, value), { endTime: v })) })))),
                exclusiveTimeSlot && !timeSlotDate && (React.createElement("div", { className: "deal-item-edit-modal-notice" }, t("Choose a date to check this option's availability."))),
                conflict && (React.createElement("div", { className: "deal-item-edit-modal-notice deal-item-edit-modal-notice--error" },
                    React.createElement("div", { className: "deal-item-edit-modal-notice-title" }, conflictTitle),
                    React.createElement("div", null, conflictHint))),
                timeSlotDate && bookings.length > 0 && (React.createElement("div", { className: "deal-item-edit-modal-bookings" },
                    React.createElement("div", { className: "deal-item-edit-modal-bookings-title" },
                        t('Time slots already booked on'),
                        ' ',
                        toFormattedDate(timeSlotDate, { timeZone: 'UTC' })),
                    React.createElement("ul", null, bookings.map((booking, index) => {
                        var _a;
                        return (React.createElement("li", { key: `${booking.deal.id}-${index}`, className: conflicting.includes(booking)
                                ? 'deal-item-edit-modal-booking deal-item-edit-modal-booking--conflict'
                                : 'deal-item-edit-modal-booking' },
                            React.createElement("span", { className: "deal-item-edit-modal-booking-time" }, formatTimeRange(booking.startTime, booking.endTime)),
                            React.createElement("span", { className: "deal-item-edit-modal-booking-body" },
                                React.createElement("a", { className: "deal-item-edit-modal-booking-deal", href: `/quotes/details/${booking.deal.id}`, target: "_blank", rel: "noopener noreferrer" }, formatBookingDeal(booking.deal)),
                                booking.via && ((_a = booking.saleItem) === null || _a === void 0 ? void 0 : _a.title) && (React.createElement("span", { className: "deal-item-edit-modal-booking-via" }, t('Through {item}, which also books this item').replace('{item}', booking.saleItem.title))))));
                    })))),
                React.createElement(FormGroup, null, customFields === null || customFields === void 0 ? void 0 : customFields.map((field) => (React.createElement(CustomFormField, { key: field.id, field: getField(field), fieldId: `${item.id}.${field.id}`, populate: true, translable: false, onChange: (fieldValue) => updateDealItemFieldValues(fieldValue, field) }))))))));
}
